import Close20 from '@carbon/icons-react/es/close/20';
import ListChecked20 from '@carbon/icons-react/es/list--checked/20';
import { Tabs, Tooltip } from 'antd';
import React, { FC, useState, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { ConversationAPI } from 'api/conversations';
import { StatusUpdateState } from 'api/status-updates';
import { ReactComponent as Conversation } from 'assets/svgs/conversation.svg';
import { OnUserMention } from 'components/molecules/mention';
import { ConversationPane } from 'components/organisms/conversations';
import { selectActiveStatusUpdate } from 'data/status-updates';
import { useUser } from 'hooks/use-user';
import { StatusUpdateHighlights } from '../highlights';
import { ConversationsEmptyState } from './tab-components';
import {
  Content,
  DocumentTabContainer,
  StyledTabs,
  Wrapper,
} from './tab-styles';

enum TabNames {
  ConversationsTab = 'conversations',
  Highlights = 'highlights',
  Close = 'close',
}

const { TabPane } = Tabs;

interface Props {
  handleConversationUserMention: OnUserMention;
  sideTabVisiblity: boolean;
  setSideTabVisiblity: (state) => void;
  onClose: () => void;
}

export const StatusUpdateTabs: FC<Props> = ({
  handleConversationUserMention,
  sideTabVisiblity,
  setSideTabVisiblity,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<TabNames>(TabNames.Highlights);
  const activeStatusUpdate = useSelector(selectActiveStatusUpdate);
  const currentUser = useUser();

  const closeDrawer = useCallback(() => {
    setSideTabVisiblity(false);
    onClose();
  }, [onClose, setSideTabVisiblity]);

  const onTabClick = useCallback(
    (_tab) => {
      if (_tab === TabNames.Close) {
        closeDrawer();
      }
      if (_tab === activeTab) {
        setSideTabVisiblity((prev: boolean) => !prev);
      }

      if (_tab !== activeTab && sideTabVisiblity) {
        setSideTabVisiblity(false);
      }
    },
    [activeTab, closeDrawer, setSideTabVisiblity, sideTabVisiblity],
  );

  const onTabChange = useCallback((tab) => {
    if ([TabNames.Highlights, TabNames.ConversationsTab].includes(tab)) {
      setActiveTab(tab as TabNames);
    }
  }, []);

  useEffect(() => {
    if (activeStatusUpdate?.state === StatusUpdateState.Published) {
      setActiveTab(TabNames.ConversationsTab);
      setSideTabVisiblity(true);
    } else {
      setActiveTab(TabNames.Highlights);
    }
  }, [activeStatusUpdate?.state, setSideTabVisiblity]);

  return (
    <DocumentTabContainer $hidePaneContent={sideTabVisiblity}>
      <StyledTabs
        destroyInactiveTabPane
        defaultActiveKey={TabNames.Highlights}
        activeKey={activeTab}
        onChange={onTabChange}
        onTabClick={onTabClick}
        tabPosition="right"
      >
        <TabPane
          key={TabNames.Close}
          tab={
            <span data-cy="status-update-drawer-close">
              <Close20 />
            </span>
          }
        />

        {activeStatusUpdate?.state !== StatusUpdateState.Published ? (
          <TabPane
            tab={
              <Tooltip
                placement="left"
                title={<FormattedMessage id={TabNames.Highlights} />}
              >
                <Content>
                  <ListChecked20 />
                </Content>
              </Tooltip>
            }
            key={TabNames.Highlights}
          >
            <Wrapper>
              <StatusUpdateHighlights />
            </Wrapper>
          </TabPane>
        ) : null}
        <TabPane
          tab={
            <Tooltip
              placement="left"
              title={<FormattedMessage id={TabNames.ConversationsTab} />}
            >
              <Content>
                <Conversation />
              </Content>
            </Tooltip>
          }
          key={TabNames.ConversationsTab}
        >
          {activeStatusUpdate?.document.documentId &&
          activeStatusUpdate?.project.projectId ? (
            <ConversationPane
              context={{
                type: 'DOCUMENT',
                id: activeStatusUpdate?.document.documentId,
              }}
              handleUserMention={handleConversationUserMention}
              projectId={activeStatusUpdate?.project.projectId}
              accountId={currentUser.account.accountId}
              apiService={ConversationAPI}
              messagesEmptyState={<ConversationsEmptyState />}
            />
          ) : null}
        </TabPane>
      </StyledTabs>
    </DocumentTabContainer>
  );
};
